import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import actions from '@src/redux/actions';
import { TIMELINE_LANE, VOLUME_TRACK_BY_LANE } from '@src/constants/timeline';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { HISTORY_LABEL, HISTORY_SCOPE } from '@src/constants/history';

/**
 * Owns each timeline track's level, keyed by lane (`media` / `voice` / `music`)
 * over `project.volumes`, so the gutter can drive its volume popover off the
 * same state. Mute is the level going to 0 — one source of truth instead of a
 * second flag that could disagree — and unmuting restores the level the track
 * had before, so a mixer setting survives a mute/unmute round trip.
 *
 * The levels are project state: auto-save persists them and the export mixes
 * them in, so this is not preview-only.
 */
const useTrackVolume = () => {
  const dispatch = useDispatch();
  const projectVolumes = useSelector((state) => state.project.volumes);

  // A mixer change is an undoable step, on the same flag and scope the voice and
  // music tracks use, so one Undo walks back through volume changes too.
  const user = useSelector((state) => state.auth.user);
  const { getFeatureValue } = useFeatureFlags();
  const undoRedoEnabled = getFeatureValue(
    FEATURE_KEYS.PROJECT_DUBBING_UNDO_REDO,
    { userId: user?.id, email: user?.email },
  );
  const historyOptions = useMemo(
    () =>
      undoRedoEnabled
        ? {
            history: {
              commit: true,
              scope: HISTORY_SCOPE.DUBBING,
              label: HISTORY_LABEL.CHANGE_TRACK_VOLUME,
            },
          }
        : {},
    [undoRedoEnabled],
  );
  // CHANGE_TRACK_VOLUME is a deferred label, so the whole drag coalesces into one
  // entry; this closes it — on slider release, and right after a mute click,
  // which is a single discrete change.
  const commitVolume = useCallback(() => {
    if (undoRedoEnabled) dispatch(actions.history.flush(HISTORY_SCOPE.DUBBING));
  }, [dispatch, undoRedoEnabled]);

  const volumes = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(VOLUME_TRACK_BY_LANE).map(([lane, track]) => [
          lane,
          projectVolumes?.[track],
        ]),
      ),
    [projectVolumes],
  );
  const lastAudibleRef = useRef({
    [TIMELINE_LANE.MEDIA]: 1,
    [TIMELINE_LANE.VOICE]: 1,
    [TIMELINE_LANE.MUSIC]: 1,
  });
  useEffect(() => {
    Object.keys(VOLUME_TRACK_BY_LANE).forEach((key) => {
      if (volumes[key] > 0) lastAudibleRef.current[key] = volumes[key];
    });
  }, [volumes]);

  const isMuted = useCallback((key) => volumes[key] === 0, [volumes]);

  // Level in 0..1 (0 while the project's volumes have not loaded yet).
  const getVolume = useCallback((key) => volumes[key] ?? 0, [volumes]);

  // Set a level directly (the reducer clamps to 0..1). A level control writes
  // the same state as mute, so 0 shows as muted and dragging up unmutes.
  const setVolume = useCallback(
    (key, value) => {
      const track = VOLUME_TRACK_BY_LANE[key];
      if (track)
        dispatch(actions.project.setTrackVolume(track, value, historyOptions));
    },
    [dispatch, historyOptions],
  );

  const toggleMute = useCallback(
    (key) => {
      const track = VOLUME_TRACK_BY_LANE[key];
      if (!track) return;
      const next = volumes[key] === 0 ? lastAudibleRef.current[key] : 0;
      dispatch(actions.project.setTrackVolume(track, next, historyOptions));
      commitVolume();
    },
    [dispatch, volumes, historyOptions, commitVolume],
  );

  return { isMuted, getVolume, setVolume, toggleMute, commitVolume };
};

export default useTrackVolume;
